/**
 * Factor Version PDF Generator
 *
 * Generates a polished, human-readable PDF from emission factor version data.
 * Uses jsPDF + jspdf-autotable for professional table rendering.
 *
 * Architecture:
 * 1. Raw version config JSON comes from the backend
 * 2. This module transforms technical data into presentation-friendly structures
 * 3. Then renders them into a multi-page PDF document
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// TYPES
// ============================================================================

interface FactorVersionData {
  factorVersion: string;
  apiVersion: string;
  methodology: string;
  createdAt: string;
  description: string;
  defaults: Record<string, any>;
  regionMapping: Record<string, string[]>;
  regionRules?: Record<string, any>;
  truckFactors: Record<string, any>;
  truckCooling: Record<string, any>;
  truckCodeMapping?: Record<string, string>;
  railFactors: Record<string, any>;
  railCoolingAdder?: Record<string, any>;
  seaFactors: Record<string, any>;
  iwwFactors: Record<string, any>;
  iwwFcpMapping?: Record<string, string>;
  ferryFactors: Record<string, any>;
  ferryTypeMapping?: Record<string, any>;
  ferryWeightMultipliers?: Record<string, any>;
  railRhWeightMultipliers?: Record<string, any>;
  transshippingFCP?: Record<string, any>;
  transshipmentFactors?: Record<string, any>;
  transshipmentRegionMapping?: Record<string, any>;
  airGenericFactors?: any;
  airFactors?: any;
  [key: string]: any;
}

// ============================================================================
// LABEL MAPPING — converts technical keys to human-readable labels
// ============================================================================

const SECTION_LABELS: Record<string, string> = {
  defaults: 'Calculation Defaults',
  regionMapping: 'Region Mapping',
  regionRules: 'Region Rules',
  truckFactors: 'Truck Emission Factors',
  truckCooling: 'Truck Cooling Adders',
  truckCodeMapping: 'Truck Code Mapping',
  railFactors: 'Rail Emission Factors',
  railCoolingAdder: 'Rail Cooling Adder',
  seaFactors: 'Sea Emission Factors',
  iwwFactors: 'Inland Waterway (IWW) Emission Factors',
  iwwFcpMapping: 'IWW Code Mapping',
  ferryFactors: 'Ferry Emission Factors',
  ferryTypeMapping: 'Ferry Type Mapping',
  ferryWeightMultipliers: 'Ferry Weight Multipliers',
  railRhWeightMultipliers: 'Rail Rolling Highway Weight Multipliers',
  transshippingFCP: 'FCP Hub Factors',
  transshipmentFactors: 'Hub Emission Factors',
  transshipmentRegionMapping: 'Hub Region Mapping',
  airGenericFactors: 'Air Emission Factors (EN Generic)',
  airFactors: 'Air Emission Factors (ISO)',
};

const FIELD_LABELS: Record<string, string> = {
  loadFactor: 'Default Load Factor (%)',
  prePostTransportLoadFactor: 'Pre/Post Transport Load Factor (%)',
  defaultFuelType: 'Default Fuel Type',
  defaultTruckCode: 'Default Truck Code',
  loadFactorNormalizationStep: 'Load Factor Normalization Step',
  compensationMultiplier: 'Compensation Multiplier (CHF/tonne)',
  totEiMultiplier: 'Emission Intensity Multiplier',
  ttw: 'TTW (Tank-to-Wheel)',
  ttwe: 'TTWE (Tank-to-Wheel + Energy)',
  wtw: 'WTW (Well-to-Wheel)',
  wtt: 'WTT (Well-to-Tank)',
  diesel: 'Diesel',
  electric: 'Electric',
  eletric: 'Electric',
  normal: 'Standard Cargo',
  reefer: 'Refrigerated Cargo',
  Dry: 'Dry Container',
  Reefer: 'Refrigerated Container',
  cooling: 'Refrigerated',
  belly: 'Belly Cargo',
  freighter: 'Freighter Aircraft',
};

function humanize(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // Don't split all-uppercase abbreviations like TTW, WTW, TTWE
  if (/^[A-Z0-9_]+$/.test(key)) return key;
  return key
    .replaceAll(/([A-Z])/g, ' $1')
    .replaceAll(/[_-]/g, ' ')
    .replaceAll(/\b\w/g, c => c.toUpperCase())
    .trim();
}

// ============================================================================
// PDF COLORS & STYLING
// ============================================================================

const COLORS = {
  primary: [144, 191, 69] as [number, number, number],
  headerBg: [144, 191, 69] as [number, number, number],
  headerText: [255, 255, 255] as [number, number, number],
  sectionBg: [245, 247, 250] as [number, number, number],
  lightGray: [240, 240, 240] as [number, number, number],
  darkText: [33, 33, 33] as [number, number, number],
  mutedText: [120, 120, 120] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  accent: [0, 150, 136] as [number, number, number],
};

// ============================================================================
// HELPER: add section title
// ============================================================================

function addSectionTitle(doc: jsPDF, title: string, y: number): number {
  if (y > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, y);
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(14, y + 2, doc.internal.pageSize.getWidth() - 14, y + 2);
  doc.setTextColor(...COLORS.darkText);
  doc.setFont('helvetica', 'normal');
  return y + 10;
}

function addSubsectionTitle(doc: jsPDF, title: string, y: number): number {
  if (y > doc.internal.pageSize.getHeight() - 30) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.accent);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkText);
  return y + 7;
}

function addNote(doc: jsPDF, text: string, y: number): number {
  if (y > doc.internal.pageSize.getHeight() - 20) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.mutedText);
  doc.setFont('helvetica', 'italic');
  doc.text(text, 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkText);
  return y + 5;
}

// ============================================================================
// SECTION RENDERERS
// ============================================================================

function renderKeyValueSection(doc: jsPDF, data: Record<string, any>, y: number): number {
  // If the data itself is an array, render as a single-column list
  if (Array.isArray(data)) {
    autoTable(doc, {
      startY: y,
      head: [['Value']],
      body: (data).map(v => [String(v)]),
      theme: 'striped',
      headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
    });
    return (doc as any).lastAutoTable.finalY + 8;
  }

  const rows = Object.entries(data).map(([key, value]) => [
    humanize(key),
    (() => {
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object' && value !== null) return JSON.stringify(value);
      return String(value);
    })()
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Setting', 'Value']],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 1: { cellWidth: 'auto' } },
    margin: { left: 14, right: 14 },
    tableWidth: 'auto',
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderRegionMapping(doc: jsPDF, data: Record<string, string[]>, y: number): number {
  const rows = Object.entries(data).map(([region, countries]) => [
    region,
    (countries).join(', ')
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Region', 'Countries']],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 9 },
    bodyStyles: { fontSize: 8 },
    columnStyles: { 1: { cellWidth: 'auto' } },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderEmissionTable(
  doc: jsPDF,
  data: Record<string, any>,
  y: number,
  columns: string[] = ['ttw', 'ttwe', 'wtw']
): number {
  const headers = ['Code', ...columns.map(c => humanize(c))];
  const rows: string[][] = [];

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Check if this is a direct emission factor row {ttw, ttwe, wtw}
      if (columns.some(c => c in value)) {
        rows.push([key, ...columns.map(c => value[c] == null ? '-' : String(value[c]))]);
      }
    }
  }

  if (rows.length === 0) return y;

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderTruckFactorsEN(doc: jsPDF, data: Record<string, any>, y: number): number {
  // EN trucks: tonnage → loadFactor → {ttw, ttwe, wtw}
  for (const [tonnage, loadFactors] of Object.entries(data)) {
    y = addSubsectionTitle(doc, `Truck Class: ${tonnage} tonnes`, y);

    const rows: string[][] = [];
    for (const [lf, factors] of Object.entries(loadFactors as Record<string, any>)) {
      rows.push([`${lf}%`, String(factors.ttw), String(factors.ttwe), String(factors.wtw)]);
    }

    autoTable(doc, {
      startY: y,
      head: [['Load Factor', 'TTW (g/tkm)', 'TTWE (g/tkm)', 'WTW (g/tkm)']],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });

    y = (doc as any).lastAutoTable.finalY + 6;
  }
  return y;
}

function renderTruckFactorsISO(doc: jsPDF, data: Record<string, any>, y: number): number {
  // ISO trucks: region → tonnage → loadFactor → {ttw, ttwe, wtw}
  for (const [region, tonnages] of Object.entries(data)) {
    y = addSubsectionTitle(doc, `Region: ${region}`, y);

    for (const [tonnage, loadFactors] of Object.entries(tonnages as Record<string, any>)) {
      const rows: string[][] = [];
      for (const [lf, factors] of Object.entries(loadFactors as Record<string, any>)) {
        rows.push([`${lf}%`, String(factors.ttw), String(factors.ttwe), String(factors.wtw)]);
      }

      autoTable(doc, {
        startY: y,
        head: [[`${tonnage}t — Load Factor`, 'TTW', 'TTWE', 'WTW']],
        body: rows,
        theme: 'striped',
        headStyles: { fillColor: COLORS.accent, textColor: COLORS.headerText, fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 4;
    }
    y += 4;
  }
  return y;
}

function renderTruckFactorsFCP(doc: jsPDF, data: Record<string, any>, y: number): number {
  // FCP trucks: truckCode → fuelType → region → loadFactor → {ttw, ttwe, wtt, wtw}
  // This is huge — summarize by showing only default fuel (Diesel) and region (EU), LF 80
  y = addNote(doc, 'Showing Diesel fuel, all regions, Load Factor 80%. Full dataset contains all fuel types and load factors.', y);

  const rows: string[][] = [];
  for (const [code, fuels] of Object.entries(data)) {
    const diesel = (fuels)['Diesel'];
    if (!diesel) continue;

    for (const [region, loadFactors] of Object.entries(diesel as Record<string, any>)) {
      const lf80 = (loadFactors)[80];
      if (!lf80) continue;
      rows.push([
        code, region,
        String(lf80.ttw ?? '-'), String(lf80.ttwe ?? '-'),
        String(lf80.wtt ?? '-'), String(lf80.wtw ?? '-')
      ]);
    }
  }

  autoTable(doc, {
    startY: y,
    head: [['Truck Code', 'Region', 'TTW', 'TTWE', 'WTT', 'WTW']],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 8 },
    bodyStyles: { fontSize: 7 },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderRailFactors(doc: jsPDF, data: Record<string, any>, y: number): number {
  const rows: string[][] = [];

  for (const [country, fuelTypes] of Object.entries(data)) {
    const d = (fuelTypes).diesel || {};
    const e = (fuelTypes).electric || (fuelTypes).eletric || {};
    rows.push([
      country,
      d.ttw == null ? '-' : String(d.ttw),
      d.ttwe == null ? '-' : String(d.ttwe),
      d.wtw == null ? '-' : String(d.wtw),
      e.ttw == null ? '-' : String(e.ttw),
      e.ttwe == null ? '-' : String(e.ttwe),
      e.wtw == null ? '-' : String(e.wtw),
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [['Country', 'Diesel TTW', 'Diesel TTWE', 'Diesel WTW', 'Electric TTW', 'Electric TTWE', 'Electric WTW']],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 7 },
    bodyStyles: { fontSize: 7 },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderSeaFactors(doc: jsPDF, data: Record<string, any>, y: number): number {
  const rows: string[][] = [];
  const hasWtt = Object.values(data).some((v: any) => v.Dry?.wtt != null);
  const cols = hasWtt ? ['ttw', 'ttwe', 'wtt', 'wtw'] : ['ttw', 'ttwe', 'wtw'];

  for (const [lane, types] of Object.entries(data)) {
    const dry = (types).Dry || {};
    const reefer = (types).Reefer || {};
    rows.push([
      lane,
      ...cols.map(c => dry[c] == null ? '-' : String(dry[c])),
      ...cols.map(c => reefer[c] == null ? '-' : String(reefer[c])),
    ]);
  }

  const dryHeaders = cols.map(c => `Dry ${c.toUpperCase()}`);
  const reeferHeaders = cols.map(c => `Reefer ${c.toUpperCase()}`);

  autoTable(doc, {
    startY: y,
    head: [['Trade Lane', ...dryHeaders, ...reeferHeaders]],
    body: rows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 7 },
    bodyStyles: { fontSize: 7 },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 8;
}

function renderAirFactorsReference(doc: jsPDF, y: number, type: 'EN' | 'ISO', airDownloadUrl: string): number {
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.darkText);
  if (type === 'EN') {
    doc.text('The EN air emission factors contain 14,000 distance-based entries (1–14,000 km).', 14, y);
    y += 7;
    doc.text('Download the full dataset:', 14, y);
    const linkX = 14 + doc.getTextWidth('Download the full dataset: ');
    doc.setTextColor(...COLORS.primary);
    doc.textWithLink('Air Emission Factors — EN', linkX, y, { url: airDownloadUrl });
    doc.setDrawColor(...COLORS.primary);
    const linkW = doc.getTextWidth('Air Emission Factors — EN');
    doc.line(linkX, y + 0.5, linkX + linkW, y + 0.5);
  } else {
    doc.text('The ISO air emission factors contain distance-based entries for Belly/Freighter aircraft across EU/NA regions.', 14, y);
    y += 7;
    doc.text('Download the full dataset:', 14, y);
    const linkX = 14 + doc.getTextWidth('Download the full dataset: ');
    doc.setTextColor(...COLORS.primary);
    doc.textWithLink('Air Emission Factors — ISO', linkX, y, { url: airDownloadUrl });
    doc.setDrawColor(...COLORS.primary);
    const linkW = doc.getTextWidth('Air Emission Factors — ISO');
    doc.line(linkX, y + 0.5, linkX + linkW, y + 0.5);
  }
  doc.setTextColor(...COLORS.darkText);
  return y + 10;
}

function renderGenericSection(doc: jsPDF, data: any, y: number, depth: number = 0): number {
  if (depth > 3) return y; // prevent infinite recursion

  if (typeof data !== 'object' || data === null) return y;

  // Arrays of primitives → render as comma-separated text
  if (Array.isArray(data)) {
    if (data.every(v => typeof v !== 'object')) {
      doc.setFontSize(9);
      const text = data.join(', ');
      const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - 28);
      doc.text(lines, 14, y);
      return y + lines.length * 4.5 + 4;
    }
    return renderKeyValueSection(doc, data, y);
  }

  // Check if all values are primitives → render as key-value table
  const entries = Object.entries(data);
  const allPrimitive = entries.every(([, v]) => typeof v !== 'object' || v === null);

  if (allPrimitive) {
    return renderKeyValueSection(doc, data, y);
  }

  // Check if values are emission factor objects {ttw, ttwe, wtw}
  const firstValue = entries[0]?.[1];
  if (typeof firstValue === 'object' && firstValue !== null && ('ttw' in firstValue || 'ttwe' in firstValue || 'wtw' in firstValue)) {
    const cols = Object.keys(firstValue);
    return renderEmissionTable(doc, data, y, cols);
  }

  // Nested — render sub-sections
  for (const [key, value] of entries) {
    y = addSubsectionTitle(doc, humanize(key), y);
    y = renderGenericSection(doc, value, y, depth + 1);
  }
  return y;
}

// ============================================================================
// MAIN PDF GENERATOR
// ============================================================================

const METADATA_KEYS = new Set(['factorVersion', 'apiVersion', 'methodology', 'createdAt', 'description']);

const SECTION_ORDER = [
  'defaults', 'regionMapping', 'regionRules',
  'truckFactors', 'truckCooling', 'truckCodeMapping',
  'railFactors', 'railCoolingAdder', 'railRhWeightMultipliers',
  'seaFactors',
  'iwwFactors', 'iwwFcpMapping',
  'ferryFactors', 'ferryTypeMapping', 'ferryWeightMultipliers',
  'airGenericFactors', 'airFactors',
  'transshippingFCP', 'transshipmentFactors', 'transshipmentRegionMapping',
];

function renderTitlePage(doc: jsPDF, data: FactorVersionData, pageWidth: number): number {
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 60, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('Emission Factor Reference', pageWidth / 2, 28, { align: 'center' });
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(`Version ${data.factorVersion} — ${data.methodology} Methodology`, pageWidth / 2, 42, { align: 'center' });
  doc.setFontSize(10);
  doc.text(data.description || '', pageWidth / 2, 52, { align: 'center' });

  // Metadata block
  doc.setTextColor(...COLORS.darkText);
  autoTable(doc, {
    startY: 75,
    body: [
      ['Factor Version', data.factorVersion],
      ['API Version', data.apiVersion],
      ['Methodology', data.methodology],
      ['Created', data.createdAt],
      ['Description', data.description],
      ['Generated', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
    ],
    theme: 'plain',
    bodyStyles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 'auto' },
    },
    margin: { left: 30, right: 30 },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

function resolveSections(data: FactorVersionData): string[] {
  const availableSections = SECTION_ORDER.filter(key => data[key] != null);
  // Also catch any keys not in SECTION_ORDER (future-proof)
  const allDataKeys = Object.keys(data).filter(k => !METADATA_KEYS.has(k) && typeof data[k] === 'object' && data[k] !== null);
  const extraKeys = allDataKeys.filter(k => !SECTION_ORDER.includes(k));
  return [...availableSections, ...extraKeys];
}

function renderTableOfContents(doc: jsPDF, sections: string[], y: number): number {
  doc.setFontSize(10);
  for (const key of sections) {
    const label = SECTION_LABELS[key] || humanize(key);
    doc.text(`• ${label}`, 20, y);
    y += 5;
    if (y > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  }
  return y;
}

function renderSection(doc: jsPDF, data: FactorVersionData, key: string, dashboardBaseUrl: string, y: number): number {
  const sectionData = data[key];
  switch (key) {
    case 'defaults':
      return renderKeyValueSection(doc, sectionData, y);
    case 'regionMapping':
      return renderRegionMapping(doc, sectionData, y);
    case 'truckFactors':
      if (data.methodology === 'EN') return renderTruckFactorsEN(doc, sectionData, y);
      if (data.methodology === 'FCP') return renderTruckFactorsFCP(doc, sectionData, y);
      return renderTruckFactorsISO(doc, sectionData, y);
    case 'truckCooling':
      return renderEmissionTable(doc, sectionData, y, data.methodology === 'FCP' ? ['ttw', 'ttwe', 'wtt', 'wtw'] : ['ttw', 'ttwe', 'wtw']);
    case 'railFactors':
      return renderRailFactors(doc, sectionData, y);
    case 'seaFactors':
      return renderSeaFactors(doc, sectionData, y);
    case 'airGenericFactors':
      return renderAirFactorsReference(doc, y, 'EN', `${dashboardBaseUrl}/shipments?airPdf=en&version=${data.factorVersion}`);
    case 'airFactors':
      return renderAirFactorsReference(doc, y, 'ISO', `${dashboardBaseUrl}/shipments?airPdf=iso&version=${data.factorVersion}`);
    case 'truckCodeMapping':
    case 'iwwFcpMapping':
    case 'railCoolingAdder':
    case 'regionRules':
      return renderKeyValueSection(doc, sectionData, y);
    default:
      // ferry/rail-rh/transshipment mappings + any future fields → generic fallback
      return renderGenericSection(doc, sectionData, y);
  }
}

function addFooters(doc: jsPDF, pageWidth: number, label: string): void {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.mutedText);
    const footerY = doc.internal.pageSize.getHeight() - 8;
    doc.text(label, 14, footerY);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, footerY, { align: 'right' });
  }
}

export function generateFactorVersionPDF(data: FactorVersionData, dashboardBaseUrl: string = ''): jsPDF {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  // ── Title Page + metadata ──
  let y = renderTitlePage(doc, data, pageWidth);

  // Table of contents
  y = addSectionTitle(doc, 'Contents', y);
  const allSections = resolveSections(data);
  renderTableOfContents(doc, allSections, y);

  // ── Content Sections ──
  doc.addPage();
  y = 20;

  for (const key of allSections) {
    const sectionData = data[key];
    if (sectionData == null) continue;

    const label = SECTION_LABELS[key] || humanize(key);
    y = addSectionTitle(doc, label, y);
    y = renderSection(doc, data, key, dashboardBaseUrl, y);
  }

  addFooters(doc, pageWidth, `CarbonCare — Emission Factor Reference v${data.factorVersion}`);
  return doc;
}

// ============================================================================
// AIR FACTORS PDF — separate document for the full air emission dataset
// ============================================================================

function renderAirTitle(doc: jsPDF, type: 'EN' | 'ISO', version: string, pageWidth: number): void {
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 50, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(`Air Emission Factors — ${type}`, pageWidth / 2, 25, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Factor Version ${version}  •  Values in g CO2 per tonne-km`, pageWidth / 2, 38, { align: 'center' });
  doc.setTextColor(...COLORS.darkText);
}

function buildAirDistanceRows(source: Record<string, any>, cols: string[]): string[][] {
  const rows: string[][] = [];
  const distances = Object.keys(source).map(Number).sort((a, b) => a - b);
  for (const dist of distances) {
    const f = source[dist];
    if (!f) continue;
    rows.push([`${dist}`, ...cols.map(c => f[c] == null ? '-' : String(f[c]))]);
  }
  return rows;
}

function renderAirEN(doc: jsPDF, airData: Record<string, any>, y: number): void {
  // EN: flat object keyed by distance → {ttw, ttwe, wtw}
  y = addNote(doc, `Complete dataset: ${Object.keys(airData).length} distance entries (1–14,000 km). Generic belly cargo factors.`, y);

  autoTable(doc, {
    startY: y,
    head: [['Distance (km)', 'TTW', 'TTWE', 'WTW']],
    body: buildAirDistanceRows(airData, ['ttw', 'ttwe', 'wtw']),
    theme: 'striped',
    headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 8 },
    bodyStyles: { fontSize: 7 },
    margin: { left: 14, right: 14 },
  });
}

function renderAirISO(doc: jsPDF, airData: Record<string, any>, y: number): void {
  // ISO: aircraftType → region → distance → {ttwe, wtt, wtw}
  for (const [aircraftType, regions] of Object.entries(airData)) {
    for (const [region, distances] of Object.entries(regions as Record<string, any>)) {
      y = addSectionTitle(doc, `${humanize(aircraftType)} — ${region} Region`, y);

      const allDistances = Object.keys(distances).map(Number).sort((a, b) => a - b);
      y = addNote(doc, `${allDistances.length} distance entries (${allDistances[0]}–${allDistances.at(-1)} km)`, y);

      autoTable(doc, {
        startY: y,
        head: [['Distance (km)', 'TTWE', 'WTT', 'WTW']],
        body: buildAirDistanceRows(distances, ['ttwe', 'wtt', 'wtw']),
        theme: 'striped',
        headStyles: { fillColor: COLORS.headerBg, textColor: COLORS.headerText, fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    }
  }
}

export function generateAirFactorsPDF(airData: any, type: 'EN' | 'ISO', version: string): jsPDF {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  renderAirTitle(doc, type, version, pageWidth);

  if (type === 'EN') {
    renderAirEN(doc, airData, 60);
  } else {
    renderAirISO(doc, airData, 60);
  }

  addFooters(doc, pageWidth, `CarbonCare — Air Emission Factors (${type}) v${version}`);
  return doc;
}
